/**
 * OPBridge discovery — LAN broadcast scan + authoritative peer walk.
 *
 * Load-bearing constraint (DEC-060): user does as little manually as
 * possible. First install, first page open of Settings → Connect: the
 * PWA finds every OPBridge on the local /24 subnet without user typing.
 *
 * Two-tier discovery (three-tier once WS silent-push lands in Phase B):
 *
 * 1. **LAN broadcast scan** — probe every /24 host on the PWA's own
 *    subnet in parallel batches. Each `GET /health` that responds within
 *    a tight budget contributes a `DiscoveredBridge` row keyed by
 *    `instance_name`. Bounded concurrency respects iPad Safari's fetch
 *    caps (~6 per origin — but we're hitting many different origins, so
 *    the effective cap is a lot higher; conservative 32 keeps latency
 *    bounded even under contention).
 *
 * 2. **Authoritative peer walk** — once at least ONE bridge is paired,
 *    call `GET /peers/paired-bridges` on it. That returns the mesh
 *    membership OPBridge is aware of via mDNS peer-sync, including peers
 *    that are currently offline. Merged in as `PeerBridge` rows.
 *
 * Merge precedence (by `instance_name` primary key):
 *   authoritative peer walk > broadcast scan > stale storage row
 *
 * URL may drift with DHCP; identity does not.
 */

import type { OpbridgeHealth } from './httpClient.ts';
import { OpbridgeHttpClient, OpbridgeError, resolveMachineName } from './httpClient.ts';

/** A bridge discovered by any means. Rows aren't stored until pairing
 *  succeeds; discovery just surfaces the mesh in the UI. */
export interface DiscoveredBridge {
  /** Stable per-instance identity from OPBridge's `/health.instance_name`
   *  (e.g. `local-GAMING-PC-sdegNg`). Primary key for merge. */
  instance_name: string;
  /** LAN base URL — `http://<ip>:<port>` (no trailing slash). */
  bridge_url: string;
  /** Human label — best available from `/health` or `display_name`. */
  display_name: string;
  /** Operator-set "who runs this bridge" label from `/health.owner`.
   *  Absent is the normal case (opt-in, never defaulted) — render
   *  conditionally, never treat a missing value as an error. */
  owner: string | null;
  /** Stable `node_id`, available from a peer-walk (`/peers/paired-bridges`)
   *  and absent for lan-scan/stored rows. Carried because it is the key the
   *  declined-peers list uses (`storage.ts`), so the Connect page can tell a
   *  peer that will auto-pair from one this device disconnected - which will
   *  never auto-pair and therefore needs a manual Connect. Do NOT join on
   *  `instance_name`; it is unstable across daemon restarts (observed live:
   *  the same Mac reported two different suffixes in one session).
   *
   *  `| undefined` is explicit, not just `?`: `exactOptionalPropertyTypes` is
   *  on, so an assigned-but-undefined value is a type error otherwise. */
  node_id?: string | undefined;
  /** HTTPS-reachable form of this bridge, when the mesh reports one. The only
   *  way a suite-hosted (HTTPS) page can probe a LAN peer - see
   *  `PairedBridgeEntry.suite_hostname`. */
  suite_url?: string | undefined;
  /** Discovery source. Higher-precedence sources win on merge. */
  source: 'peer-walk' | 'lan-scan' | 'stored';
  /** True when the source has confirmed the bridge is actually reachable
   *  right now (peer-walk without a live probe = false; lan-scan hits =
   *  true; stored rows = false until connection manager confirms). */
  reachable: boolean;
}

const HEALTH_TIMEOUT_MS = 250;
const SCAN_CONCURRENCY = 32;
const OPBRIDGE_DEFAULT_PORT = 8765;
// port+2 / port+3 off the main 8765 (DEC-166's Phase 3) — see suiteHostedCandidates().
const OPBRIDGE_LOOPBACK_PORT = 8767;
const OPBRIDGE_LOCAL_HTTPS_PORT = 8768;
const SUITE_HOSTED_PROBE_TIMEOUT_MS = 1500;

/** Best-effort derivation of the client's own LAN /24 subnet from the
 *  PWA's current origin. Two paths:
 *
 * 1. If `document.location.hostname` is a LAN IP (typical when the PWA
 *    is served by a Vite dev server bound to `0.0.0.0` and the user
 *    opened it via the LAN IP on iPad), derive the /24 from that.
 * 2. If the hostname is `localhost` / `127.0.0.1` / a name we can't
 *    parse to an IP, fall back to a heuristic: scan the "well-known"
 *    home-router /24s (`192.168.1.0/24`, `192.168.0.0/24`,
 *    `192.168.50.0/24`, `10.0.0.0/24`). This is a rough bootstrap for
 *    the dev-PC case where the PWA is opened on the same machine.
 *
 * Returns an array of `/24` subnet prefixes (e.g. `"192.168.50."`).
 * Callers append `.1`..`.254` to build candidate host IPs.
 */
export function guessLanSubnets(): string[] {
  const subnets = new Set<string>();
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/.exec(host);
    if (m) {
      subnets.add(`${m[1]}.${m[2]}.${m[3]}.`);
    }
  }
  // Well-known fallback subnets in case the PWA is on loopback and can't
  // self-derive. Common home / studio ranges.
  subnets.add('192.168.50.');
  subnets.add('192.168.1.');
  subnets.add('192.168.0.');
  return Array.from(subnets);
}

/** Probe a single candidate URL. Returns a `DiscoveredBridge` if
 *  `/health` responds within budget, otherwise null. `timeoutMs` defaults to
 *  the tight broadcast-scan budget; suiteHostedCandidates() passes a more
 *  generous one since it only ever tries three fixed URLs, not 254. */
async function probeHost(baseUrl: string, signal: AbortSignal, timeoutMs: number = HEALTH_TIMEOUT_MS): Promise<DiscoveredBridge | null> {
  const client = new OpbridgeHttpClient(baseUrl, null, timeoutMs);
  try {
    // Race the aggregate scan abort against the per-request timeout so
    // caller-side cancellation propagates quickly (e.g. user closes the
    // Connect page mid-scan).
    if (signal.aborted) return null;
    const h: OpbridgeHealth = await client.health();
    if (!h.ok || h.app !== 'OPBridge') return null;
    const instance_name = h.instance_name ?? '';
    if (!instance_name) return null;
    const display_name = resolveMachineName(h) ?? instance_name;
    return {
      instance_name,
      bridge_url: baseUrl,
      display_name,
      owner: h.owner ?? null,
      source: 'lan-scan',
      reachable: true,
    };
  } catch (_err) {
    return null;
  }
}

/**
 * Probe a known set of base URLs directly. Returns whichever answered.
 *
 * This exists so the Connect page can stop broadcast-sweeping for addresses it
 * ALREADY HAS. A bridge that is stored but momentarily disconnected - OPBridge
 * restarting, a Wi-Fi blip, a laptop waking - used to fall through to a full
 * 254-host sweep despite its exact `baseUrl` sitting in storage. That is both
 * absurd on its face and actively harmful: the sweep's socket storm starves
 * the very reconnect it is trying to make (measured: a live sweep turned a
 * ~5ms request into an 18.2s one).
 *
 * Timeout is deliberately generous relative to the sweep's 250ms. That budget
 * is tuned for discarding 254 hosts that mostly do not exist; here every URL is
 * one we have already paired with, so a false negative costs a pointless
 * broadcast sweep. Real bridges answered in 3-8ms in every measurement this
 * session, so 1200ms is ~150x headroom and still fails fast enough to fall
 * back within one scan tick.
 */
const KNOWN_URL_PROBE_TIMEOUT_MS = 1_200;

export async function probeKnownUrls(
  urls: string[],
  signal?: AbortSignal,
): Promise<DiscoveredBridge[]> {
  const sig = signal ?? new AbortController().signal;
  const hits = await Promise.all(
    urls.map((u) => probeHost(u, sig, KNOWN_URL_PROBE_TIMEOUT_MS)),
  );
  const seen = new Set<string>();
  const out: DiscoveredBridge[] = [];
  for (const h of hits) {
    if (!h || seen.has(h.instance_name)) continue;
    seen.add(h.instance_name);
    out.push(h);
  }
  return out;
}

/** Run a bounded-concurrency scan of every host in the given /24
 *  subnets. Returns every OPBridge that answered `/health` in time.
 *
 *  `onFound` fires the moment each new bridge answers, so callers can
 *  surface it immediately instead of waiting out the whole sweep. Without
 *  it, a bridge that responds in ~500ms stays hidden until the last of the
 *  ~762 probes times out (~6s) — the "Connect takes a while" complaint. */
export async function scanLan(
  subnets: string[] = guessLanSubnets(),
  signal?: AbortSignal,
  onFound?: (bridge: DiscoveredBridge) => void,
  /**
   * Stop the sweep the moment ANY bridge answers (default true).
   *
   * MEASURED 2026-08-28. The sweep holds `SCAN_CONCURRENCY` sockets against
   * dead hosts, and that starves real requests. Collateral latency to a live
   * bridge during a sweep, 120 samples per level, same machine:
   *
   *   conc  >500ms  >1s     max     full sweep
   *      4       0    0     8ms        6521ms
   *      8       1    1  2010ms        4266ms
   *     16       4    3  3217ms        3953ms
   *     32       5    3  1497ms        1941ms
   *     64      14    1  1520ms        1016ms
   *
   * Only concurrency 4 is clean, but at 4 a bridge at `.51` is not even
   * PROBED for ~3s (51 hosts / 4 workers x 250ms), which trades a rare stall
   * for guaranteed sluggishness on every first run. Lowering concurrency is
   * the wrong knob.
   *
   * Stopping at the first hit is the right one: it cuts the exposure window
   * rather than the throughput, so discovery stays fast AND the socket storm
   * ends early. Nothing is lost, because a single reachable bridge is all the
   * operator needs to act on - once it is paired, `walkPeers` returns the rest
   * of the mesh authoritatively and `inheritanceDrainer` adopts them with no
   * further scanning. A broadcast sweep was never the mechanism that found
   * the SECOND bridge; it just paid for it.
   *
   * Pass `false` for an exhaustive sweep (a diagnostic wanting every host).
   */
  stopOnFirstHit = true,
): Promise<DiscoveredBridge[]> {
  const abortCtrl = new AbortController();
  const sig = signal ?? abortCtrl.signal;
  const targets: string[] = [];
  for (const subnet of subnets) {
    for (let host = 1; host <= 254; host += 1) {
      targets.push(`http://${subnet}${host}:${OPBRIDGE_DEFAULT_PORT}`);
    }
  }
  const results: DiscoveredBridge[] = [];
  const seen = new Set<string>();

  // Simple worker pool: N in flight at once, refill on each completion.
  let cursor = 0;
  let foundOne = false;
  async function worker(): Promise<void> {
    while (true) {
      if (sig.aborted || (stopOnFirstHit && foundOne)) return;
      const i = cursor;
      cursor += 1;
      const target = targets[i];
      if (!target) return;
      const hit = await probeHost(target, sig);
      if (hit && !seen.has(hit.instance_name)) {
        seen.add(hit.instance_name);
        results.push(hit);
        // Set BEFORE onFound so sibling workers stop on their next loop
        // rather than after another full 250ms probe each.
        foundOne = true;
        onFound?.(hit);
      }
    }
  }
  const workers = Array.from({ length: SCAN_CONCURRENCY }, () => worker());
  await Promise.all(workers);
  return results;
}

/** Walk the mesh via one paired bridge's `/peers/paired-bridges`.
 *  Returns every peer the bridge knows about (may include offline ones).
 *  These rows carry `reachable: false` until a subsequent live probe
 *  confirms them — peer-walk is a mesh-membership statement, not a
 *  liveness statement. */
export async function walkPeers(client: OpbridgeHttpClient): Promise<DiscoveredBridge[]> {
  try {
    const resp = await client.pairedBridges();
    return resp.bridges.map((p) => ({
      instance_name: p.instance_name,
      bridge_url: p.bridge_url,
      display_name: p.display_name,
      node_id: p.node_id,
      suite_url: p.suite_hostname
        ? `https://${p.suite_hostname}:${p.browser_tls_port ?? 8768}`
        : undefined,
      // Peer-walk is mesh-membership only (no live /health read per peer),
      // so `owner` isn't available here — null, not a guess.
      owner: null,
      source: 'peer-walk',
      reachable: false,
    }));
  } catch (err) {
    if (err instanceof OpbridgeError) {
      // Bearer scope may not include /peers/paired-bridges on legacy
      // tokens (e.g. the retired DEV_PC_BEARER predates this endpoint).
      // Silent no-op — the Connect page falls back to lan-scan.
      return [];
    }
    throw err;
  }
}

export { isOnSuiteDomain } from '../util/env.ts';

/**
 * Ordered LAN-reachability candidates for a suite-hosted PWA (DEC-166 Phase 3).
 * OPBridge already runs two extra listeners alongside the main 8765 port
 * specifically for this (confirmed generic + live 2026-08-12, zero new
 * OPBridge work needed — see `.notepads/opid-subdomain-plan.md`):
 *   - port+2 (8767): CORS-enabled loopback listener. `127.0.0.1`/`localhost`
 *     are mixed-content-EXEMPT even over plain `http://`, so this covers
 *     "browser and OPBridge on the same machine" with zero cert friction.
 *   - port+3 (8768): `opbridge.local` HTTPS with an auto-minted self-signed
 *     cert, mDNS-resolvable. Covers "a DIFFERENT LAN device" (e.g. an iPad
 *     hitting the dev PC's OPBridge) — the operator trusts the cert once
 *     (`GET /pairing/ipad-ca.pem`), then it works offline indefinitely.
 * Order matters (same-machine case is far more common and has zero
 * cert-trust friction, so it's tried first) — this is NOT a parallel race
 * like scanLan(), it's three fixed, ordered attempts.
 */
export function suiteHostedCandidates(): string[] {
  return [
    `http://127.0.0.1:${OPBRIDGE_LOOPBACK_PORT}`,
    `http://localhost:${OPBRIDGE_LOOPBACK_PORT}`,
    `https://opbridge.local:${OPBRIDGE_LOCAL_HTTPS_PORT}`,
  ];
}

/** Try each suiteHostedCandidates() URL in order, first `/health` hit wins.
 *  Returns null if none answer (fresh install with nothing paired yet, or
 *  genuinely no reachable OPBridge — caller falls back to manual URL entry,
 *  which already works for any scheme per DEC-166's Phase 3 notes). */
export async function probeSuiteHostedCandidates(signal?: AbortSignal): Promise<DiscoveredBridge | null> {
  const sig = signal ?? new AbortController().signal;
  for (const url of suiteHostedCandidates()) {
    if (sig.aborted) return null;
    const hit = await probeHost(url, sig, SUITE_HOSTED_PROBE_TIMEOUT_MS);
    if (hit) return hit;
  }
  return null;
}

/** Merge discovery results from multiple sources into a single
 *  `instance_name`-keyed list. Precedence: peer-walk > lan-scan >
 *  stored. Reachability: any source that reports `reachable: true`
 *  wins over `false` for the merged row. */
export function mergeDiscovered(...sources: DiscoveredBridge[][]): DiscoveredBridge[] {
  const order: DiscoveredBridge['source'][] = ['peer-walk', 'lan-scan', 'stored'];
  const byInstance = new Map<string, DiscoveredBridge>();
  const rank = (s: DiscoveredBridge['source']): number => order.indexOf(s);
  for (const arr of sources) {
    for (const row of arr) {
      if (!row.instance_name) continue;
      const existing = byInstance.get(row.instance_name);
      if (!existing) {
        byInstance.set(row.instance_name, { ...row });
        continue;
      }
      // Higher-precedence source wins the row identity + display_name,
      // but the reachable bit is a strict OR across sources, and `owner`
      // prefers whichever source actually has one — peer-walk is
      // mesh-membership only and never carries it (always null), so letting
      // it win outright would silently drop a real lan-scan-sourced owner.
      const useIncoming = rank(row.source) < rank(existing.source);
      const merged: DiscoveredBridge = useIncoming
        ? { ...row, reachable: row.reachable || existing.reachable, owner: row.owner ?? existing.owner }
        : { ...existing, reachable: existing.reachable || row.reachable, owner: existing.owner ?? row.owner };
      byInstance.set(row.instance_name, merged);
    }
  }
  return Array.from(byInstance.values());
}
